import { QuestionsService } from '@APP/services/questions-service';
import { css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { Events } from '../../events/app-events';
import { DepInjMixin, requires } from '../../mixins/dependency';
import { UserService } from '../../services/user-service';
import { eventBus } from '../../utils/event-bus';
import { BlackWhatsappIcon, WhatsappIcon, testBadge } from '../icons/icons';
import { t } from '../../utils/locale';
import { DeviceAwareComponent } from './device-aware-component';
import { ConfigService } from '@APP/services/config-service';
import { PopupService } from '@APP/services/popup-service';

type Deps = {
  userService: UserService;
  configService: ConfigService;
  questionsService: QuestionsService;
  popupService: QuestionsService;
};

@requires(UserService.serviceName, ConfigService.serviceName, QuestionsService.serviceName, PopupService.serviceName)
@customElement('lh-header-line')
export class LHHeaderLine extends DepInjMixin<Deps>(DeviceAwareComponent) {
  private unsubs: Function[] = [];

  @state()
    username: string = '';

  @state()
    agent: string = '';

  // reactive so the badge appears when the application (and its flag) loads
  @state()
    isAgentDraft: boolean = false;

  @query('#redirect-link')
    link!: HTMLElement;

  @property({ type: Boolean, attribute: 'black', reflect: true })
    black: boolean = false;

  static get baseStyles() {
    return [
      css`
        :host-context([keyboard-open]) #wrapper { height: 0; }
        :host(.section-changing) #sections-wrapper {
          user-select: none;
        }
      #wrapper {
        overflow: hidden;
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 2.75rem;
        height: 3rem;
        max-height: calc(var(--current-header-height) + 0.5rem);
        margin-bottom: 2rem;
        transition: var(--transition-speed, 350ms) var(--transition-switch);
        transition-property: height;
        cursor:pointer;
      }
      #whatsapp-wrapper {
        width: 3rem;
        height: 3rem;
        position: absolute;
        right: 3.22rem;
      }
      #agent, #username {
        font-weight: bold;
        font-size: 3rem;
        color: var(--c-emphasize-sec);
      }
      #user-line {
        display: flex;
        align-items: baseline;
        gap: 0.3em;
        flex-wrap: wrap;
        justify-content: center;
        max-width: 100%;
        padding-inline: 1rem;
        box-sizing: border-box;
      }
      /* a long unbroken name must wrap instead of pushing the sidebar out */
      #username {
        min-width: 0;
        overflow-wrap: anywhere;
        text-align: center;
      }
      /* agent sessions only — the customer funnel keeps the larger name */
      #user-line.with-agent #agent,
      #user-line.with-agent #username {
        font-size: 2rem;
      }
      .test-badge {
        display: flex;
        flex: none;
      }
      .test-badge svg {
        width: 7rem;
        height: auto;
      }
      #wrapper > a + span {
        margin-top: -1.5rem;
      }
      .icon-whatsapp {
        height: 3rem;
        width: 3rem;
      }
    `,
    ];
  };
  static get desktopStyles() {
    return [
      css`
    #whatsapp-wrapper {
      display: none !important;
    }
    #wrapper {
      height: 18rem;
      flex-direction: column;
    }
    :host([agent-draft]) #wrapper {
      height: 28rem;
    }
    `,
    ];
  };
  static get mobileStyles() {
    return [
      css`
    #wrapper {
      height: calc(var(--current-header-height) - 12rem);
      gap : 10px;
     }
    :host([current-section="S_Welcome"]) #wrapper { 
      height: calc(var(--current-header-height) - 10rem); 
      filter: unset;
    }

    #agent, #username {
      margin-top: -1.5rem;
      font-size: 2.5rem;
    }
    #agent {
      margin-right: 0.3em;
    }
    .test-badge svg {
      width: 5rem;
    }
    #whatsapp-wrapper{
      display: none;
    }
    `,
    ];
  };

  connectedCallback() {
    super.connectedCallback();
    this.unsubs.push(
      eventBus().on<void>(Events.Type.Refresh, this.checkLoginState.bind(this)),
      eventBus().on(Events.Type.SectionChanged, (section) => {
        this.setAttribute('current-section', section.sectionId);
        this.checkLoginState();
      }),
    );
    this.checkLoginState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubs.forEach((f) => f());
  }

  willUpdate() {
    this.toggleAttribute('agent-draft', this.isAgentDraft);
  }

  renderContent() {
    const { questionsService } = this.deps;
    const affiliate = questionsService.getAffiliateValue();
    return html`
      <div id="wrapper" dir="ltr">
        <span id="whatsapp-wrapper" @click=${this.handleWhatsappClick}>
          ${this.black ? BlackWhatsappIcon : WhatsappIcon}
        </span>
          <lh-logo @click=${questionsService.showBeforeExitPopup} .affiliate=${affiliate}></lh-logo>
          <div id="user-line" class=${this.agent ? 'with-agent' : ''}>
            ${this.agent ? html`<span id="agent">${this.agent}</span>` : nothing}
            ${affiliate ?'' :html`<span id="username">${this.username}</span>`}
          </div>
          ${this.isAgentDraft ? html`<div class="test-badge" role="img" aria-label=${t('T_Draft_Application')}>${testBadge}</div>` : nothing}
      </div>
    `;
  }

  private handleWhatsappClick() {
    window.open('https://wa.me/972775712722', '_blank');
  }

  private checkLoginState() {
    this.isAgentDraft = this.deps.questionsService.isAgentDraftApplication();
    this.username = this.deps.userService.getNameTitle();
    this.agent = this.deps.userService.userType == 'agent' ? 'Internal Agent' : this.deps.userService.userType == 'externalAgent' ? 'Agent' : '';
  }
}
